import { useEffect, useState } from 'react';
import { useAppointment } from '../../../context/appointmentContext';
import { client } from '../../../helpers/api-client';
import { useTestContext } from '../../../context/testContext';
import { useResultData } from '../../../helpers/useResultData';
import { formattedRefractionScore } from '../../../helpers/refraction';
import { EyeResultsTable, SessionActions, useSessionUI } from '../../../ui';
import { StepBody, useConfirmLeave } from '../testScreenKit';

// Sphere / cylinder / axis per eye, formatted exactly as the old RefractionEyeScore did.
export const refractionScoreCells = (score) => {
  if (!score || typeof score !== 'object') return ['N/A', 'N/A', 'N/A'];
  return [formattedRefractionScore(score.sphere, 2) || 'N/A', formattedRefractionScore(score.cylinder, 2) || 'N/A', formattedRefractionScore(score.axis, 0) || 'N/A'];
};

// Step 4 - results (Figma session/17 middle, landscape 22 left) + save confirm (17 right).
const RefractionResults = ({ localResults, setActiveTab, updateStep }) => {
  const { confirm, toast } = useSessionUI();
  const confirmLeave = useConfirmLeave();
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState([]);
  const { appointment } = useAppointment();
  const { clearAllResults } = useResultData();
  const {
    testData: { testResultsId },
  } = useTestContext();

  const getTestResults = (testId) => {
    client(`api/appointment-test-result/${testId}`).then(
      ({ result_data }) => {
        if (result_data) setResults(JSON.parse(result_data));
      },
      (error) => {
        console.error(error);
      },
    );
  };

  useEffect(() => {
    if (!localResults) return getTestResults(testResultsId);
    if (localResults) return setResults(localResults);
  }, [localResults, testResultsId]);

  const saveTestResults = async () => {
    const { corrected } = results;
    let testResultData = { leftEye: 'Eye not tested', rightEye: 'Eye not tested', corrected };
    if (results.testRightEye && results.rightEye) {
      testResultData['rightEye'] = results.rightEye;
    }

    if (results.testLeftEye && results.leftEye) {
      testResultData['leftEye'] = results.leftEye;
    }

    if (results.testBothEyes && results.bothEyes) {
      testResultData['bothEyes'] = results.bothEyes;
    }

    const sendTestResultData = {
      appointment_eye_test: results.eyeTestId,
      appointment: appointment.currentAppointment.id,
      result_data: JSON.stringify(testResultData),
    };

    if ((await confirm({ message: 'Are you sure you want to save the test results?' })) === true) {
      setSaving(true);
      client(`api/appointment-test-result/`, { body: sendTestResultData }).then(
        () => {
          setSaving(false);
          toast({ tone: 'success', title: 'Results saved', body: 'Refraction results were added to this appointment.' });
          setActiveTab('results');
        },
        (error) => {
          setSaving(false);
          console.error('There was an error saving the results - ', error);
          toast({ tone: 'error', title: 'Could not save the results', body: 'Please try again.' });
        },
      );
    }
  };

  const restartTest = () => {
    confirmLeave('restart').then(() => {
      clearAllResults();
      return updateStep('eyeSetup');
    });
  };

  const eyes = results.bothEyes ? [['Binocular', results.bothEyes]] : [['Right Eye', results?.rightEye], ['Left Eye', results?.leftEye]];
  const cells = eyes.map(([, eye]) => refractionScoreCells(eye?.score));
  const rowFor = (label, i) => ({ cells: [<b key='l'>{label}</b>, ...cells.map((c) => c[i])] });

  return (
    <StepBody center data-testid='refraction-results'>
      <div style={{ width: '100%', maxWidth: 640, alignSelf: 'center' }}>
        <EyeResultsTable columns={['', ...eyes.map(([label]) => label)]} gap={12} rows={[rowFor('Sphere', 0), { ...rowFor('Cylinder', 1), tone: 'muted' }, rowFor('Axis', 2)]} />
      </div>
      <SessionActions
        onBack={() => (localResults ? restartTest() : setActiveTab('results'))}
        backLabel={localResults ? 'Restart' : 'Back'}
        primary={localResults ? { label: saving ? 'Saving…' : 'Save Test Results', onClick: () => saveTestResults(), disabled: saving } : undefined}
      />
    </StepBody>
  );
};

export default RefractionResults;
